import { ColorAliases } from '@mds/common';
import * as d3 from 'd3';

type D3Selection<T extends d3.BaseType> = d3.Selection<T, unknown, HTMLElement, any>;

export class SankyLegend {
  private readonly itemSpacing = 32;
  private readonly bgPadding = 12;
  private group: D3Selection<SVGGElement>;
  private bg: D3Selection<SVGRectElement>;
  private expandedBgHeight?: number;
  private lastX?: number;
  private lastSankeyHeight?: number;
  private itemNode = 0;
  private itemEdge = 0;
  private gradientData: any;

  constructor(
    private svg: D3Selection<SVGSVGElement>,
    private data: any[]
  ) {
    this.group = svg.append('g');
    this.bg = this.group.append('rect');
    this.renderItems();
    this.renderBg();
  }

  setPosition(x: number, y: number, sankeyHeight) {
    this.lastX = x;
    this.lastSankeyHeight = sankeyHeight;
    const legendHeight = this.group.node().getBBox().height + this.itemSpacing;
    this.group.attr('transform', `translate(${x}, ${sankeyHeight - legendHeight})`);
  }

  private updatePosition() {
    if (this.lastX !== undefined && this.lastSankeyHeight !== undefined) {
      const legendHeight = this.group.node().getBBox().height + this.itemSpacing;
      this.group.attr('transform', `translate(${this.lastX}, ${this.lastSankeyHeight - legendHeight})`);
    }
  }

  private renderItems() {
    let isCollapsed = false; // Track collapse state
    this.gradientData = this.data.find((e) => e.type === 'gradient');
    // Append legend title
    const title = this.group
      .append('text')
      .attr('x', 16)
      .attr('y', -10)
      .attr('class', 'legend-title')
      .style('font-size', '14px')
      .style('font-weight', '500')
      .style('fill', ColorAliases.CONTENT_SECONDARY) // Set font color
      .text('LEGEND')
      .style('cursor', 'pointer');

    // Append Dropdown Icon (SVG)
    const dropdownIcon = this.group
      .append('image')
      .attr('x', 158) // Position next to the text
      .attr('y', -23)
      .attr('width', 16)
      .attr('height', 16)
      .attr('href', 'assets/icons/system/global/expand_more.svg') // Default down arrow
      .style('cursor', 'pointer');
    // Toggle Functionality
    const toggleLegend = () => {
      isCollapsed = !isCollapsed;
      legendItems.style('display', isCollapsed ? 'none' : 'block');
      this.group.select('.legend-footnote').style('display', isCollapsed ? 'none' : 'block');
      this.bg.style('height', isCollapsed ? '40px' : `${this.getGroupRect().height + this.bgPadding * 2}px`);
      const expandedHeight = this.expandedBgHeight ?? this.getGroupRect().height + this.bgPadding * 2;
      this.bg.attr('height', isCollapsed ? 40 : expandedHeight);

      // Change the icon
      dropdownIcon.attr(
        'href',
        isCollapsed ? 'assets/icons/system/global/expand_less.svg' : 'assets/icons/system/global/expand_more.svg'
      );

      // Re-position after DOM/layout updates so the legend bottom stays aligned
      setTimeout(() => this.updatePosition(), 0);
    };

    setTimeout(() => {
      const legendWidth = this.group.node().getBBox().width; // Get legend width dynamically
      dropdownIcon.attr('x', legendWidth - 30); // Align at right corner
    }, 300);

    // Attach Click Event to Both Title & Icon
    title.on('click', toggleLegend);
    dropdownIcon.on('click', toggleLegend);
    const legendItems = this.group.append('g');

    legendItems
      .append('text')
      .attr('x', 16)
      .attr('y', 16) // Positioned below the first title
      .attr('class', 'legend-subtitle')
      .style('font-size', '1rem')
      .style('font-weight', '500')
      .style('fill', ColorAliases.CONTENT_GREY)
      .text('Symbols');
    this.gradientData = this.data.find((e) => e.type === 'gradient');

    const items = legendItems
      .selectAll('.legend')
      .data(this.data.reverse())
      .enter()
      .append('g')
      .attr('class', 'legend')
      .attr('transform', (d) => {
        if (this.hasTypeOrIsUnknown(d.type, d.name)) {
          return `translate(162, ${this.itemEdge++ * this.itemSpacing})`;
        }
        return `translate(0, ${this.itemNode++ * this.itemSpacing})`;
      });

    items.each((d, i, nodes) => {
      const item = d3.select(nodes[i]); // Ensure correct selection of current element

      if (d.name === 'Edges' || d.name === 'Should cost' || d.name === 'Market price') {
        item
          .append('rect')
          .attr('x', 8)
          .attr('y', 22)
          .attr('width', 24)
          .attr('height', 24)
          .attr('fill', this.getColor(d)); // Change color as needed
      } else {
        item
          .append('image')
          .attr('x', 8)
          .attr('y', 22)
          .attr('width', 26)
          .attr('height', 26)
          .attr('clip-path', 'circle(12px at center)') // Ensures rounded image
          .attr('href', () => this.getIcon(d)); // Use arrow function to retain `this`
      }
    });

    items
      .append('text')
      .attr('x', (d) => (this.isNodesOrEdges(d.name) ? 16 : 40))
      .attr('y', 34)
      .attr('dy', '.35em')
      .style('text-anchor', 'start')
      .text((d) => d.name);

    // append a defs (for definition) element to your SVG
    if (!this.gradientData) return;
    items
      .append('text')
      .attr('x', 50)
      .attr('y', 62)
      .attr('dy', '.35em')
      .style('display', (d) => (d.type === 'gradient' ? 'block' : 'none'))
      .style('text-anchor', 'start')
      .text(`\u2264${this.gradientData.max_value}%`);
    const svgLegend = items
      .append('svg')
      .attr('width', 50)
      .attr('height', 100)
      .style('display', (d) => (d.type === 'gradient' ? 'block' : 'none'));
    const defs = svgLegend.append('defs');

    // append a linearGradient element to the defs and give it a unique id
    const linearGradient = defs.append('linearGradient').attr('id', 'linear-gradient');

    // vertical gradient
    linearGradient.attr('x1', '0%').attr('y1', '100%').attr('x2', '0%').attr('y2', '0%');

    // Empty objects added because, In the Linear Gradient, We have added color codes, and by default
    // loop starts from the length(eg. 4) of group data we have added above ".selectAll('.legend').data(this.data.reverse())"",
    // So empty objects added at the beginning of array(equals data length). So that all color codes can be added in the Gradient.
    const emptyObjects = [];
    this.data.forEach((item) => {
      emptyObjects.push({});
    });

    // starting height of legend Gradient
    let count = 2.7;

    // append multiple color stops by using D3's data/enter step
    linearGradient
      .data([
        ...emptyObjects,
        { offset: '100%', color: '#01c9b3' },
        { offset: '90%', color: '#49d981' },
        { offset: '80%', color: '#7de45d' },
        { offset: '70%', color: '#b1ef38' },
        { offset: '60%', color: '#e3fa15' },
        { offset: '50%', color: '#fff101' },
        { offset: '40%', color: '#ffd101' },
        { offset: '30%', color: '#ffb101' },
        { offset: '20%', color: '#ff8f01' },
        { offset: '10%', color: '#ff5f00' }
      ])
      .enter()
      .append('rect')
      .attr('x', 180)
      .attr('y', (d, i) => {
        const value = count + count * 7;
        count++;
        return value + 38;
      })
      .attr('width', 25)
      .attr('height', 8)
      .style('fill', (d) => d.color);

    items
      .append('text')
      .attr('x', 50)
      .attr('y', 134)
      .attr('dy', '.35em')
      .style('display', (d) => (d.type === 'gradient' ? 'block' : 'none'))
      .style('text-anchor', 'start')
      .text(`\u2264${this.gradientData.min_value}%`);

    this.group
      .append('text')
      .attr('x', 15)
      .attr('y', 194)
      .attr('class', 'legend-footnote')
      .text('The height of the nodes represents mass flow')
      .append('tspan')
      .attr('dy', '1.5em');
  }
  private getIcon(d) {
    let icon = '';
    if (d.nltype === 'product') {
      icon = 'assets/icons/composer-data-objects/PG_legend.svg';
    } else if (d.nltype === 'process') {
      icon = 'assets/icons/composer-data-objects/Process_legend.svg';
    } else if (d.nltype === 'commodity') {
      icon = 'assets/icons/composer-data-objects/Commodity_legend.svg';
    } else if (d.nltype === 'has_alternatives') {
      icon = 'assets/icons/composer-data-objects/alternative_available_legend.svg';
    } else if (d.nltype === 'alternative') {
      icon = 'assets/icons/composer-data-objects/alternative_applied_legend.svg';
    } else if (d.name === 'Nodes') {
      icon = 'assets/icons/composer-data-objects/nodes_legend.svg';
    }
    return icon;
  }
  private getColor(d) {
    let color = '';
    if (d.name === 'Edges') {
      color = '#7F7F7F';
    } else if (d.name === 'Should cost') {
      color = '#E8C395';
    } else if (d.name === 'Market price') {
      color = '#C2C2C2';
    }
    return color;
  }
  private renderBg() {
    const { x, y, width, height } = this.getGroupRect();

    this.bg
      .attr('x', x - this.bgPadding)
      .attr('y', y - this.bgPadding)
      .attr('width', width + this.bgPadding * 2)
      .attr('height', height + this.bgPadding * 2)
      .attr('rx', 4)
      .style('fill', ColorAliases.NEUTRAL_BACKGROUND)
      .style('stroke', ColorAliases.BORDER_UNIVERSAL) // Set border color
      .style('stroke-width', 1)
      .style('opacity', 1);
    this.expandedBgHeight = height + this.bgPadding * 2;
  }

  private getGroupRect() {
    return this.group.node().getBBox();
  }

  private hasTypeOrIsUnknown(type: string, name: string) {
    return type || name === 'Unknown';
  }

  private isNodesOrEdges(name: string) {
    return name === 'Industrial Data Objects' || name === 'Recycled Content' || name === 'Recyclability';
  }
}
